import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ExternalLink,
  Globe,
  MapPin,
  Search,
  ShoppingBag,
  Store
} from 'lucide-react';
import axiosInstance from '../../axiosConfig';
import { getMarketplaceUrl, getStoreUrl } from '../../config/platformConfig';
import './Marketplace.css';

export const isMarketplaceHost = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname || '';
  return hostname === 'marketplace.localhost' || hostname.startsWith('marketplace.');
};

export const getMarketplacePath = (path = '/') => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (isMarketplaceHost()) return normalized;
  return normalized === '/' ? '/marketplace' : `/marketplace${normalized}`;
};

export const getMarketplaceProductPath = (product, campaignId = '') => {
  const basePath = getMarketplacePath(`/product/${product.store?.slug}/${product.slug}`);
  if (!campaignId) return basePath;
  return `${basePath}?campaign=${encodeURIComponent(campaignId)}`;
};

export const marketplaceCurrency = (value, currency = 'NGN') => {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(amount);
  } catch (_) {
    return `${currency} ${amount.toLocaleString()}`;
  }
};

export const getStorefrontUrl = (store) => {
  if (store?.customDomain) return `https://${store.customDomain}`;
  return getStoreUrl(store?.slug);
};

export const getStorefrontLabel = (store) => {
  const url = getStorefrontUrl(store);
  if (!url) return store?.slug || 'Revstar store';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (_) {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '');
  }
};

export const formatMerchantLocation = (merchant) => {
  const city = merchant?.location?.city?.trim();
  const state = merchant?.location?.state?.trim();
  const country = merchant?.location?.country?.trim();
  if (city && state) return `${city}, ${state}`;
  if (state) return country ? `${state}, ${country}` : state;
  if (city) return country ? `${city}, ${country}` : city;
  return country || 'Location not set';
};

export const getMerchantInitials = (name = '') =>
  name
    .split(' ')
    .map((part) => part.trim()[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'R';

const trackedImpressions = new Set();

const trackCampaignMetric = async (campaignId, metric) => {
  if (!campaignId) return;
  try {
    await axiosInstance.post('/marketplace/boosts/track', {
      metric,
      campaignIds: [campaignId]
    });
  } catch (_) {
    // Silent by design. Tracking should not interrupt browsing.
  }
};

export const trackSponsoredClick = (campaignId) => trackCampaignMetric(campaignId, 'click');
export const trackSponsoredProductView = (campaignId) => trackCampaignMetric(campaignId, 'productView');
export const trackSponsoredImpression = (campaignId, placementKey = 'default') => {
  const impressionKey = `${campaignId}:${placementKey}`;
  if (!campaignId || trackedImpressions.has(impressionKey)) return;
  trackedImpressions.add(impressionKey);
  trackCampaignMetric(campaignId, 'impression');
};

const useSponsoredImpression = (campaignId) => {
  const ref = useRef(null);
  const placementKeyRef = useRef(`placement-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    const placementKey = placementKeyRef.current;
    if (!campaignId || !ref.current || trackedImpressions.has(`${campaignId}:${placementKey}`)) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      trackSponsoredImpression(campaignId, placementKey);
      return undefined;
    }

    const node = ref.current;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35);
      if (!visible) return;
      trackSponsoredImpression(campaignId, placementKey);
      observer.disconnect();
    }, { threshold: [0.35, 0.6] });

    observer.observe(node);
    return () => observer.disconnect();
  }, [campaignId]);

  return ref;
};

export const MarketplaceHeader = ({ active = 'home' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search]);

  const submitSearch = (event) => {
    event.preventDefault();
    const query = search.trim();
    if (!query) {
      navigate(getMarketplacePath('/merchants'));
      return;
    }
    navigate(`${getMarketplacePath('/merchants')}?search=${encodeURIComponent(query)}`);
  };

  return (
    <header className="marketplace-header">
      <div className="marketplace-header-inner">
        <Link className="marketplace-brand" to={getMarketplacePath('/')} aria-label="Revstar Marketplace home">
          <img className="marketplace-brand-mark" src="/logo192.png" alt="" />
          <span className="marketplace-brand-wordmark">Marketplace</span>
        </Link>

        <form className="marketplace-header-search" onSubmit={submitSearch}>
          <label>
            <Search size={18} />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search products, stores, or categories"
            />
          </label>
          <button type="submit" className="marketplace-header-search-btn" aria-label="Search marketplace">
            <Search size={18} />
          </button>
        </form>

        <nav className="marketplace-header-actions" aria-label="Marketplace navigation">
          <Link className={`marketplace-nav-link ${active === 'home' ? 'is-active' : ''}`} to={getMarketplacePath('/')} aria-label="Marketplace">
            <ShoppingBag size={18} />
            <span className="marketplace-sr-only">Marketplace</span>
          </Link>
          <Link className={`marketplace-nav-link ${active === 'merchants' ? 'is-active' : ''}`} to={getMarketplacePath('/merchants')} aria-label="Stores">
            <Store size={18} />
            <span className="marketplace-sr-only">Stores</span>
          </Link>
          {!isMarketplaceHost() ? (
            <a className="marketplace-nav-link" href={getMarketplaceUrl()} aria-label="Open marketplace host">
              <ExternalLink size={18} />
              <span className="marketplace-sr-only">Open marketplace host</span>
            </a>
          ) : null}
        </nav>
      </div>
    </header>
  );
};

export const SponsoredBadge = ({ label = 'Sponsored' }) => (
  <span className="marketplace-sponsored-badge">{label}</span>
);

export const MarketplaceProductImage = ({ src, alt }) => {
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [src]);

  if (!src || broken) {
    return (
      <div className="marketplace-image-fallback" role="img" aria-label={alt || 'Product image unavailable'}>
        <svg
          className="marketplace-image-fallback-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
          <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt || ''}
      onError={() => setBroken(true)}
      loading="lazy"
      decoding="async"
    />
  );
};

const getWebsiteBaseUrl = () => {
  if (typeof window === 'undefined') return 'https://userevstar.com';
  const hostname = window.location.hostname || '';
  const protocol = window.location.protocol === 'http:' ? 'http:' : 'https:';
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    return `${protocol}//localhost:3000`;
  }
  const parts = hostname.split('.');
  const root = parts.length >= 2 ? parts.slice(-2).join('.') : hostname;
  return `${protocol}//${root}`;
};

export const MarketplaceFooter = () => {
  const marketplaceHomeUrl = getMarketplaceUrl();
  const websiteRoot = getWebsiteBaseUrl();
  const year = new Date().getFullYear();

  return (
    <footer className="marketplace-footer" aria-label="Marketplace footer">
      <div className="marketplace-footer-inner">
        <div className="marketplace-footer-brand">
          <div className="marketplace-footer-brand-row">
            <img className="marketplace-footer-mark" src="/logo192.png" alt="" />
            <span className="marketplace-footer-wordmark">Marketplace</span>
          </div>
          <p className="marketplace-footer-blurb">
            Discover live Revstar stores and shop directly from independent merchants.
          </p>
        </div>

        <div className="marketplace-footer-columns">
          <div className="marketplace-footer-col">
            <h4>Shop</h4>
            <Link to={getMarketplacePath('/')}>Marketplace home</Link>
            <Link to={getMarketplacePath('/merchants')}>Browse stores</Link>
            <a href={marketplaceHomeUrl}>Open marketplace host</a>
          </div>

          <div className="marketplace-footer-col">
            <h4>Sell on Revstar</h4>
            <a href={`${websiteRoot}/pricing`}>Pricing &amp; plans</a>
            <a href={`${websiteRoot}/features`}>Merchant features</a>
            <a href={`${websiteRoot}/use-cases`}>Use cases</a>
          </div>

          <div className="marketplace-footer-col">
            <h4>Company</h4>
            <a href={`${websiteRoot}/about`}>About Revstar</a>
            <a href={`${websiteRoot}/contact`}>Contact</a>
            <a href={`${websiteRoot}/blog`}>Blog</a>
          </div>

          <div className="marketplace-footer-col">
            <h4>Legal</h4>
            <a href={`${websiteRoot}/terms`}>Terms of service</a>
            <a href={`${websiteRoot}/privacy`}>Privacy policy</a>
          </div>
        </div>
      </div>

      <div className="marketplace-footer-bottom">
        <span>© {year} Revstar. All rights reserved.</span>
        <span>Powered by Revstar.</span>
      </div>
    </footer>
  );
};

export const MarketplaceSectionHeading = ({
  eyebrow,
  title,
  description,
  actionLabel,
  actionTo
}) => (
  <div className="marketplace-section-head">
    <div className="marketplace-section-copy">
      {eyebrow ? <span className="marketplace-section-eyebrow">{eyebrow}</span> : null}
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
    </div>
    {actionLabel && actionTo ? (
      <Link className="marketplace-inline-action" to={actionTo}>
        {actionLabel}
        <ArrowRight size={16} />
      </Link>
    ) : null}
  </div>
);

const MerchantCoverFallback = () => (
  <div className="marketplace-image-fallback marketplace-image-fallback--store" role="img" aria-label="Merchant storefront">
    <svg
      className="marketplace-image-fallback-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 9l1-5h16l1 5" />
      <path d="M4 9h16v11H4z" />
      <path d="M9 20v-6h6v6" />
    </svg>
  </div>
);

export const MerchantStoreCard = ({ merchant, compact = false }) => {
  const location = formatMerchantLocation(merchant);
  const productCount = Number(merchant?.productCount || 0);
  const summary = merchant.description || `Shop ${merchant.name}'s live storefront and continue directly into the merchant checkout flow.`;

  return (
    <article className={`marketplace-merchant-card${compact ? ' marketplace-merchant-card--compact' : ''}`}>
      <Link className="marketplace-merchant-card-cover" to={getMarketplacePath(`/merchant/${merchant.slug}`)}>
        {merchant.heroImage ? <img src={merchant.heroImage} alt={merchant.name} /> : <MerchantCoverFallback />}
      </Link>

      <div className="marketplace-merchant-card-body">
        <div className="marketplace-merchant-card-head">
          <div className="marketplace-merchant-card-logo">
            {merchant.logo ? <img src={merchant.logo} alt={merchant.name} /> : <span>{getMerchantInitials(merchant.name)}</span>}
          </div>
          <div className="marketplace-merchant-card-title">
            <h3>{merchant.name}</h3>
            <span>{getStorefrontLabel(merchant)}</span>
          </div>
        </div>

        <p className="marketplace-merchant-card-description">
          {summary}
        </p>

        <div className="marketplace-merchant-card-footer">
          <div className="marketplace-merchant-card-meta">
            <span><MapPin size={14} /> {location}</span>
            <span><ShoppingBag size={14} /> {productCount} products</span>
            <span><Globe size={14} /> Live storefront</span>
          </div>

          <div className="marketplace-merchant-card-actions">
            <Link className="marketplace-text-link" to={getMarketplacePath(`/merchant/${merchant.slug}`)}>
              View store
              <ArrowRight size={15} />
            </Link>
            <a className="marketplace-subtle-link" href={getStorefrontUrl(merchant)} target="_blank" rel="noreferrer">
              Visit live shop
            </a>
          </div>
        </div>
      </div>
    </article>
  );
};

export const MarketplaceStoreChip = ({ merchant }) => (
  <Link className="marketplace-store-chip" to={getMarketplacePath(`/merchant/${merchant.slug}`)}>
    <div className="marketplace-store-chip-logo">
      {merchant.logo ? <img src={merchant.logo} alt={merchant.name} /> : <span>{getMerchantInitials(merchant.name)}</span>}
    </div>
    <div className="marketplace-store-chip-copy">
      <strong>{merchant.name}</strong>
      <span>{getStorefrontLabel(merchant)}</span>
    </div>
    <ArrowRight size={15} />
  </Link>
);

// Product-card-adjacent store tile. Used in the "Featured stores" strip on
// the marketplace home, between Marketplace picks and the category rows.
// Shares dimensions + hover behaviour with ProductCard so the two card
// families read as one visual system, but the media area carries a logo
// overlay and the body swaps "price" for store-specific meta.
export const MarketplaceStoreTile = ({ merchant }) => {
  const location = formatMerchantLocation(merchant);
  const productCount = Number(merchant?.productCount || 0);
  const storefrontLabel = getStorefrontLabel(merchant);

  return (
    <Link className="marketplace-store-tile" to={getMarketplacePath(`/merchant/${merchant.slug}`)}>
      <div className="marketplace-store-tile-media">
        {merchant.heroImage ? (
          <img src={merchant.heroImage} alt={merchant.name} loading="lazy" decoding="async" />
        ) : (
          <MerchantCoverFallback />
        )}
      </div>
      <div className="marketplace-store-tile-body">
        <div className="marketplace-store-tile-logo" aria-hidden="true">
          {merchant.logo ? (
            <img src={merchant.logo} alt="" loading="lazy" decoding="async" />
          ) : (
            <span>{getMerchantInitials(merchant.name)}</span>
          )}
        </div>
        <div className="marketplace-store-tile-topline">
          <span className="marketplace-store-tile-kicker">Storefront</span>
          {location ? <span className="marketplace-store-tile-location">{location}</span> : null}
        </div>
        <strong className="marketplace-store-tile-name">{merchant.name}</strong>
        <div className="marketplace-store-tile-meta">
          <span>{productCount} {productCount === 1 ? 'product' : 'products'}</span>
          <span>·</span>
          <span>{storefrontLabel}</span>
        </div>
        <span className="marketplace-store-tile-cta">
          View store <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
};

export const ProductCard = ({ product }) => {
  const productUrl = getMarketplaceProductPath(product, product.sponsorship?.campaignId);
  const impressionRef = useSponsoredImpression(product.sponsorship?.campaignId);
  const priceNow = Number(product.price || 0);
  const priceCompare = Number(product.compareAtPrice || 0);
  const comparePriceVisible = priceCompare > priceNow;
  const discountPercent = product.sponsorship && comparePriceVisible && priceCompare > 0
    ? Math.round(((priceCompare - priceNow) / priceCompare) * 100)
    : 0;
  const merchantLabel = product.store?.name || 'Merchant storefront';
  const stockLabel = product.hasVariations
    ? (product.quantity > 0 ? 'Variants available' : 'Variants out of stock')
    : (product.quantity > 0 ? `${product.quantity} available` : 'Out of stock');

  // "NEW" pill for products created within the last 14 days. Sponsored
  // takes precedence — if the card already has a Sponsored badge in the
  // top-left, skip the NEW pill so we don't stack labels.
  const NEW_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
  const createdAtMs = product.createdAt ? new Date(product.createdAt).getTime() : 0;
  const showNewBadge = !product.sponsorship
    && Number.isFinite(createdAtMs)
    && createdAtMs > 0
    && (Date.now() - createdAtMs) <= NEW_WINDOW_MS;

  return (
    <article className="marketplace-product-card" ref={impressionRef}>
      <Link
        className="marketplace-product-card-media"
        to={productUrl}
        onClick={() => trackSponsoredClick(product.sponsorship?.campaignId)}
      >
        {product.sponsorship ? <SponsoredBadge label={product.sponsorship.label} /> : null}
        {showNewBadge ? (
          <span className="marketplace-product-card-new" aria-label="New product">NEW</span>
        ) : null}
        {discountPercent > 0 ? (
          <span className="marketplace-product-card-discount" aria-label={`${discountPercent}% off`}>
            -{discountPercent}%
          </span>
        ) : null}
        <MarketplaceProductImage src={product.image} alt={product.name} variant="card" />
      </Link>

      <div className="marketplace-product-card-body">
        <div className="marketplace-product-card-topline">
          <span className="marketplace-product-card-store">{product.category || 'General'}</span>
          <span className={`marketplace-stock-pill${product.quantity > 0 ? ' marketplace-stock-pill--ok' : ''}`}>{stockLabel}</span>
        </div>

        <Link
          className="marketplace-product-card-title"
          to={productUrl}
          onClick={() => trackSponsoredClick(product.sponsorship?.campaignId)}
        >
          {product.name}
        </Link>

        <div className="marketplace-product-card-price">
          <strong>{marketplaceCurrency(product.price, product.currency)}</strong>
          {comparePriceVisible ? <s>{marketplaceCurrency(product.compareAtPrice, product.currency)}</s> : null}
        </div>

        <div className="marketplace-product-card-meta">
          <span className="marketplace-product-card-merchant">{merchantLabel}</span>
          <span>{getStorefrontLabel(product.store)}</span>
        </div>
      </div>
    </article>
  );
};

export const ProductRailCard = ({ product }) => {
  const productUrl = getMarketplaceProductPath(product, product.sponsorship?.campaignId);
  const impressionRef = useSponsoredImpression(product.sponsorship?.campaignId);

  return (
    <Link
      ref={impressionRef}
      className="marketplace-product-rail-card"
      to={productUrl}
      onClick={() => trackSponsoredClick(product.sponsorship?.campaignId)}
    >
      <div className="marketplace-product-rail-media">
        {product.sponsorship ? <SponsoredBadge label={product.sponsorship.label} /> : null}
        <MarketplaceProductImage src={product.image} alt={product.name} variant="card" />
      </div>
      <div className="marketplace-product-rail-body">
        <span>{product.store?.name || 'Storefront'}</span>
        <h3>{product.name}</h3>
        <strong>{marketplaceCurrency(product.price, product.currency)}</strong>
      </div>
    </Link>
  );
};

export const useMarketplaceProductGroups = (products = []) =>
  useMemo(() => {
    const sponsored = products.filter((product) => product?.sponsorship);
    const organic = products.filter((product) => !product?.sponsorship);
    const ordered = [...sponsored, ...organic];

    return {
      heroProduct: ordered[0] || null,
      featuredGrid: ordered.slice(0, 20),
      railProducts: ordered.slice(0, 32),
      spotlightProducts: ordered.slice(1, 5),
      sponsoredProducts: sponsored,
      organicProducts: organic
    };
  }, [products]);

export const MarketplacePromoCard = ({ product }) => {
  if (!product) return null;

  return (
    <Link
      className="marketplace-promo-card"
      to={getMarketplaceProductPath(product, product.sponsorship?.campaignId)}
      onClick={() => trackSponsoredClick(product.sponsorship?.campaignId)}
    >
      <div className="marketplace-promo-card-media">
        <MarketplaceProductImage src={product.image} alt={product.name} variant="promo" />
      </div>
      <div className="marketplace-promo-card-copy">
        {product.sponsorship ? <SponsoredBadge label={product.sponsorship.label} /> : <span className="marketplace-section-eyebrow">Marketplace pick</span>}
        <h3>{product.name}</h3>
        <p>{product.shortDescription || product.description || 'Inspect this product, compare pricing, then continue inside the merchant’s live storefront to complete checkout.'}</p>
        <div className="marketplace-promo-card-footer">
          <div>
            <span>{product.store?.name}</span>
            <strong>{marketplaceCurrency(product.price, product.currency)}</strong>
          </div>
          <span className="marketplace-inline-action">
            Open product
            <ArrowRight size={15} />
          </span>
        </div>
      </div>
    </Link>
  );
};

export const MarketplaceTrustPill = ({ children }) => (
  <span className="marketplace-trust-pill">{children}</span>
);

export const MarketplaceExternalStoreLink = ({ store, children }) => (
  <a className="marketplace-text-link" href={getStorefrontUrl(store)} target="_blank" rel="noreferrer">
    {children}
    <ExternalLink size={15} />
  </a>
);
